import { Component, OnInit, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SiteActions } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { NitrogenModelProperties, SiteState } from '../shared/models';
import {
  GetSoilTypes,
  GetUnitsOfMeasure,
  GetNutrientDesignations,
  GetNitrogenModelProperties,
  DeleteNitrogenModelProperties,
  OpenCreateNitrogenModelPropertyDialog,
  OpenEditNitrogenModelPropertyDialog,
} from '../store/actions';

import { selectNitrogenModelProperties } from '../store/selectors';

enum NitrogenModelPropertiesAction {
  edit = 'edit',
  remove = 'remove',
}

@Component({
  selector: 'app-nitrogen-model-properties',
  templateUrl: './nitrogen-model-properties.component.html',
  styleUrls: ['./nitrogen-model-properties.component.scss'],
  standalone: false,
})
export class NitrogenModelPropertiesComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);

  private readonly subscription = new Subscription();
  title = this.locale.translate('nitrogenModelProperties.title');
  displayedColumns: string[] = [
    'nutrientDesignation',
    'soilType',
    'soilTargetUoM',
    'nBasebuffer',
    'maxNbs',
    'maxNbsSeedPotato',
    'actions',
  ];
  nitrogenModelPropertiesActions = [
    {
      icon: 'edit',
      viewValue: this.locale.translate('edit'),
      key: NitrogenModelPropertiesAction.edit,
    },
    {
      icon: 'delete',
      viewValue: this.locale.translate('remove'),
      key: NitrogenModelPropertiesAction.remove,
    },
  ];
  dataSource = new MatTableDataSource<NitrogenModelProperties>();
  dataAvailable = false;
  isMobile$ = this.breakpoint.ltLg$;

  ngOnInit(): void {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: this.title }));
    this.store.dispatch(new GetNitrogenModelProperties());
    this.store.dispatch(new GetSoilTypes());
    this.store.dispatch(new GetUnitsOfMeasure());
    this.store.dispatch(new GetNutrientDesignations());

    const selectNitrogenModelPropertiesSubscription = this.store
      .pipe(select(selectNitrogenModelProperties))
      .subscribe((result) => {
        this.dataSource.data = result;
        this.dataAvailable = result.length > 0;
        this.cdr.detectChanges();
      });

    this.subscription.add(selectNitrogenModelPropertiesSubscription);

    this.updateData();
  }

  updateData() {
    this.store.dispatch(new GetNitrogenModelProperties());
  }

  onPage() {
    this.updateData();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  createNitrogenModelProperty() {
    this.store.dispatch(new OpenCreateNitrogenModelPropertyDialog());
  }

  nitrogenModelPropertiesAction(key: string, id: number) {
    switch (key) {
      case NitrogenModelPropertiesAction.edit:
        this.editNitrogenModelProperty(id);
        break;
      case NitrogenModelPropertiesAction.remove:
        this.removeNitrogenModelProperty(id);
        break;
    }
  }

  editNitrogenModelProperty(id: number) {
    const nitrogenModelProperties = this.dataSource.data.find((np) => np.id === id);
    this.store.dispatch(new OpenEditNitrogenModelPropertyDialog({ nitrogenModelProperties }));
  }

  removeNitrogenModelProperty(id: number) {
    this.store.dispatch(new DeleteNitrogenModelProperties(id));
  }
}
